"use client";

import React, { useState } from "react";
import { Copy, Check, Phone, Mail, MapPin, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface ContactCardProps {
  icon: React.ElementType;
  title: string;
  value: string;
  index: number;
}
function ContactCard({
  icon: Icon,
  title,
  value,
  index
}: ContactCardProps) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(value).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };
  return <Card data-index={index} className="flex flex-row items-center p-5 gap-5 shadow-sm hover:shadow-md transition-all duration-300 border-border/50 bg-card rounded-lg">
      <div className="bg-primary/10 p-3 rounded-md text-primary shrink-0">
        <Icon className="size-6" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-default font-medium text-muted-foreground mb-1">{title}</p>
        <p className="font-default font-semibold text-foreground truncate">{value}</p>
      </div>
      <TooltipProvider delayDuration={100}>
        <Tooltip open={copied ? true : undefined}>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={handleCopy} className="shrink-0 hover:bg-primary/5 hover:text-primary transition-colors" aria-label={`Kopiera ${title.toLowerCase()}`}>
              {copied ? <Check className="size-4 text-green-600 dark:text-green-400" /> : <Copy className="size-4 text-muted-foreground" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="bg-foreground text-background">
            <p className="text-xs font-default font-medium">{copied ? "Kopierad!" : "Kopiera"}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </Card>;
}
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="139295" className="flex flex-col items-center justify-center p-12 text-center bg-accent/30 border border-primary/20 rounded-lg h-full min-h-[400px] animate-in fade-in zoom-in-95 duration-500">
        <div className="size-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
          <Check className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground mb-2">Tack för din anmälan!</h3>
        <p className="text-muted-foreground mb-8 max-w-md text-balance font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary/5 font-default uppercase text-sm tracking-wider">
          Gör en ny anmälan
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Ditt namn är obligatoriskt",
        minLength: {
          value: 2,
          message: "Namnet måste vara minst 2 tecken"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-wider text-muted-foreground">Fullständigt namn</FormLabel>
              <FormControl>
                <Input placeholder="Erik Andersson" {...field} className="bg-background border-input focus-visible:ring-primary rounded-lg transition-colors" />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-postadress är obligatorisk",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ogiltig e-postadress"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-wider text-muted-foreground">E-postadress</FormLabel>
              <FormControl>
                <Input type="email" placeholder="erik@exempel.se" {...field} className="bg-background border-input focus-visible:ring-primary rounded-lg transition-colors" />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" rules={{
      required: "Stad är obligatoriskt"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-wider text-muted-foreground">Stad</FormLabel>
            <FormControl>
              <Input type="text" placeholder="Stockholm" {...field} className="bg-background border-input focus-visible:ring-primary rounded-lg transition-colors" />
            </FormControl>
            <FormDescription className="text-xs font-default">Ange staden där du planerar att operera.</FormDescription>
            <FormMessage className="text-xs" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Information om erfarenhet är obligatoriskt",
      minLength: {
        value: 10,
        message: "Beskrivningen måste vara minst 10 tecken"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-wider text-muted-foreground">Erfarenhet & Motivation</FormLabel>
            <FormControl>
              <Textarea placeholder="Berätta kort om din bakgrund inom transport eller teknik..." className="min-h-[120px] resize-y bg-background border-input focus-visible:ring-primary rounded-lg transition-colors" {...field} />
            </FormControl>
            <FormMessage className="text-xs" />
          </FormItem>} />

      <FormField control={control} name="preferred_contact" render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-default text-xs uppercase tracking-wider text-muted-foreground">Nuvarande vagnparksdrift</FormLabel>
            <FormControl>
              <div className="flex flex-wrap gap-4">
                {["Ingen", "Mindre (1-5)", "Större (5+)"].map((option, i) => <label key={option} data-index={i} className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200 ${field.value === option ? "border-primary bg-primary/5 ring-0 shadow-sm" : "border-input bg-background hover:bg-accent/50 hover:border-accent-foreground/20"}`}>
                    <div className={`size-4 rounded-full border flex items-center justify-center ${field.value === option ? "border-primary" : "border-muted-foreground"}`}>
                      {field.value === option && <div className="size-2 rounded-full bg-primary" />}
                    </div>
                    <input type="radio" className="sr-only" {...field} value={option} checked={field.value === option} />
                    <span className="text-sm font-default font-medium">{option}</span>
                  </label>)}
              </div>
            </FormControl>
            <FormMessage className="text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm flex items-start gap-3">
          <div className="mt-0.5"></div>
          <p className="font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] h-12 text-sm font-default font-medium uppercase tracking-wider bg-primary text-primary-foreground hover:bg-primary/90 transition-all rounded-lg group">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-5" />
            Skickar...
          </> : <>
            Skicka ansökan
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}
export default function TalkToASpecialistToday() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const contactDetails = [{
    icon: Phone,
    title: "Support",
    value: "+46 8 555 123 45"
  }, {
    icon: Mail,
    title: "Ansökningar",
    ocean: "operator@robotaxi.se",
    value: "operator@robotaxi.se"
  }, {
    icon: MapPin,
    title: "Huvudkontor",
    value: "Innovation Hub, Stockholm"
  }];
  return <section id="anmäl-intresse" className="bg-secondary text-foreground py-24 lg:py-32 relative overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          
          {}
          <div className={`lg:col-span-5 space-y-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center rounded-lg border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-default font-medium text-primary uppercase tracking-widest">
                <span className="flex size-1.5 rounded-full bg-primary mr-2 animate-pulse"></span>
                Ansökan öppen
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground text-balance">
                Bli en del av framtidens mobilitet
              </h2>
              <p className="text-lg text-muted-foreground max-w-md text-balance leading-relaxed font-default">
                Registrera ditt intresse för att bli en Robotaxi-operatör. Vi söker partners som vill vara med och forma nästa generation av autonoma transporter.
              </p>
            </div>

            <div className="space-y-4">
              {contactDetails.map((detail, index) => <ContactCard key={index} index={index} icon={detail.icon} title={detail.title} value={detail.value} />)}
            </div>
            
            <div className="pt-6 border-t border-border/50">
              <p className="text-sm text-muted-foreground font-default">
                <strong className="font-semibold text-foreground">Handläggningstid:</strong> Vi granskar ansökningar löpande.
              </p>
            </div>
          </div>

          {}
          <div className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-card rounded-lg p-6 sm:p-10 shadow-md hover:shadow-lg transition-shadow border border-border/50 relative overflow-hidden">
              {}
              <div className="absolute top-0 left-0 w-full h-1 bg-primary opacity-80" />
              
              <div className="mb-8">
                <h3 className="text-2xl font-default font-semibold text-foreground mb-2">Anmäl intresse</h3>
                <p className="text-muted-foreground font-default">Fyll i formuläret nedan så kontaktar en av våra specialister dig för ett inledande samtal.</p>
              </div>

              <FormProvider formKey="interest_form_google81d70fe24024ae2a" formId="interest_form_google81d70fe24024ae2a" sectionName="Anmäl intresse" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: "",
              preferred_contact: "Ingen"
            }} submitText="Skicka ansökan" successMessage="Tack för din ansökan! En av våra partnerspecialister kommer att kontakta dig inom kort för att diskutera nästa steg." errorMessage="Ett fel uppstod när ansökan skulle skickas. Vänligen kontrollera dina uppgifter eller kontakta oss direkt via e-post.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}